import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Plus, Trash2, Home } from "lucide-react";
import { toast } from "sonner";

const empty = { label: "Home", full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" };

const Addresses = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/addresses"); setList(data); }
    catch { setList([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    for (const k of ["full_name", "phone", "line1", "city", "state", "pincode"]) {
      if (!form[k]) { toast.error("Please complete the address"); return; }
    }
    setSaving(true);
    try {
      await api.post("/addresses", form);
      toast.success("Address saved");
      setForm(empty); setShowForm(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/addresses/${id}`);
    load();
  };

  return (
    <div className="mx-auto max-w-4xl px-6 md:px-10 py-12" data-testid="addresses-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="tick-label mb-2">My addresses</div>
          <h1 className="font-heading text-4xl text-[#2C3E2D] font-medium">Saved delivery addresses</h1>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="btn-primary rounded-full" data-testid="add-address-btn">
          <Plus className="h-4 w-4 mr-1" /> {showForm ? "Cancel" : "New address"}
        </Button>
      </div>

      {showForm && (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 mb-8" data-testid="new-address-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="af-label" />
            <Field label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="af-name" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="af-phone" />
            <Field label="Pincode" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} testid="af-pincode" />
            <Field full label="Address line 1" value={form.line1} onChange={(v) => setForm({ ...form, line1: v })} testid="af-line1" />
            <Field full label="Address line 2 (optional)" value={form.line2} onChange={(v) => setForm({ ...form, line2: v })} testid="af-line2" />
            <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="af-city" />
            <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="af-state" />
          </div>
          <Button onClick={save} disabled={saving} className="btn-primary rounded-full mt-5 h-11 px-6" data-testid="save-address-btn">
            {saving ? "Saving…" : "Save address"}
          </Button>
        </div>
      )}

      {list.length === 0 && !showForm ? (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white p-16 text-center" data-testid="addresses-empty">
          <MapPin className="mx-auto h-6 w-6 text-[#81B29A] mb-2" />
          <p className="text-[#6B7B6C]">You haven&apos;t saved any addresses yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="addresses-list">
          {list.map((a) => (
            <div key={a.address_id} className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={`address-row-${a.address_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#81B29A] font-semibold">
                  <Home className="h-3.5 w-3.5" /> {a.label || "Home"}
                </div>
                <button onClick={() => del(a.address_id)} className="text-[#6B7B6C] hover:text-red-500" data-testid={`del-addr-${a.address_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 text-[#2C3E2D] font-medium">{a.full_name} · {a.phone}</div>
              <div className="text-sm text-[#6B7B6C] mt-1">
                {a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />
                {a.city}, {a.state} — {a.pincode}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, testid, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-xs uppercase tracking-widest text-[#6B7B6C]">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 h-11 rounded-xl bg-white" data-testid={testid} />
  </div>
);

export default Addresses;
